import { Link } from "react-router-dom";
import { Truck, ShieldCheck, Award } from "lucide-react";

const HERO_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/static/homepage/hero_recovery.png`;

/**
 * Hero for the new business model: auto spares + vehicle recovery.
 * Matches the reference mockup structure — bold split headline, sub-copy,
 * two CTAs (Shop Auto Parts / Request Recovery) and a highlight strip.
 */
const HomeHero = () => (
  <section className="vd-home-hero" data-testid="home-hero">
    <div className="vd-home-hero-media" aria-hidden="true">
      <img src={HERO_IMG} alt="ValueDrive recovery truck carrying a saloon car with auto parts arranged beside it" />
      <div className="vd-home-hero-overlay" />
    </div>

    <div className="vd-container relative">
      <div className="vd-home-hero-copy">
        <h1 className="vd-home-hero-title" data-testid="home-hero-title">
          Your Trusted Source for<br />
          Quality <span className="vd-red">Auto Spares</span> &amp;<br />
          Vehicle <span className="vd-red">Recovery Services</span>
        </h1>
        <p className="vd-home-hero-sub" data-testid="home-hero-sub">
          Supplying genuine and quality used auto parts, tyres, batteries, and providing reliable vehicle recovery services across the UK.
        </p>
        <div className="flex flex-wrap gap-3 mt-6">
          <Link to="/parts" className="vd-btn-primary" data-testid="home-hero-shop-parts">
            SHOP AUTO PARTS
          </Link>
          <Link to="/recovery" className="vd-btn-secondary vd-hero-secondary" data-testid="home-hero-request-recovery">
            REQUEST RECOVERY
          </Link>
        </div>
      </div>

      <div className="vd-home-hero-highlights" data-testid="home-hero-highlights">
        <HighlightIcon icon={<Award size={20} />} title="QUALITY PARTS" sub="Tested & Guaranteed" testid="highlight-quality" />
        <HighlightIcon icon={<Truck size={20} />} title="FAST DELIVERY" sub="Nationwide Delivery" testid="highlight-delivery" />
        <HighlightIcon icon={<ShieldCheck size={20} />} title="TRUSTED SERVICE" sub="Customer Focused" testid="highlight-service" />
      </div>
    </div>

    <style>{`
      .vd-home-hero { position: relative; overflow: hidden; background: #000; color: #fff; min-height: 620px; padding: 80px 0 120px; }
      .vd-home-hero-media { position: absolute; inset: 0; }
      .vd-home-hero-media img { width: 100%; height: 100%; object-fit: cover; object-position: center right; opacity: 0.85; }
      .vd-home-hero-overlay { position: absolute; inset: 0; background: linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.65) 45%, rgba(0,0,0,0.25) 100%); }
      .vd-home-hero-copy { position: relative; max-width: 640px; }
      .vd-home-hero-title { color: #fff !important; font-weight: 900; line-height: 1.05; font-size: clamp(34px, 5.2vw, 56px); margin: 0 0 20px; letter-spacing: -0.01em; }
      .vd-home-hero-title .vd-red { color: var(--primary-red); }
      .vd-home-hero-sub { color: rgba(255,255,255,0.85); font-size: 17px; line-height: 1.55; max-width: 560px; margin: 0; }
      .vd-hero-secondary { background: rgba(255,255,255,0.08) !important; color: #fff !important; border-color: rgba(255,255,255,0.35) !important; backdrop-filter: blur(6px); }
      .vd-hero-secondary:hover { background: rgba(255,255,255,0.16) !important; }
      .vd-home-hero-highlights { position: relative; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; margin-top: 56px; max-width: 640px; }
      @media (max-width: 640px) {
        .vd-home-hero { padding: 56px 0 80px; }
        .vd-home-hero-media img { object-position: 70% center; }
        .vd-home-hero-highlights { grid-template-columns: 1fr; gap: 12px; margin-top: 40px; }
      }
    `}</style>
  </section>
);

const HighlightIcon = ({ icon, title, sub, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <span className="inline-flex items-center justify-center rounded-full" style={{ width: 46, height: 46, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(230,0,0,0.6)", color: "var(--primary-red)" }}>
      {icon}
    </span>
    <div className="leading-tight">
      <div className="font-extrabold text-[13px] tracking-wider">{title}</div>
      <div className="text-[12px]" style={{ color: "rgba(255,255,255,0.75)" }}>{sub}</div>
    </div>
  </div>
);

export default HomeHero;
